import {
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconClearAll, IconSearch } from "@tabler/icons-react";
import { HEADER_HEIGHT } from "components/layouts/consts";
import { useProgressiveDebtorFetch } from "hooks/useProgressiveDebtorFetch";
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DebtorSummaries } from "types/model.types";
import { debounce } from "utils/helper";
import DebtorCard from "./DebtorCard";

const CustomerSearch: React.FC = () => {
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [filteredDebtors, setFilteredDebtors] = useState<DebtorSummaries>([]);
  const [searchParams] = useSearchParams();
  const refreshFlag = searchParams.get("r");

  // loads all debtor for quick search... this has small foot print becuase it only bring<
  // back summary and there's less than 1000 records>
  const {
    debtors,
    loading: debtorLoading,
    error: debtorError,
    refresh,
  } = useProgressiveDebtorFetch();

  const debouncedSearch = useMemo(
    () =>
      debounce((query: string, list: DebtorSummaries) => {
        const q = query.toLowerCase().trim();

        if (!q) {
          setFilteredDebtors([]);
          setSearchOpen(false);
          return;
        }

        // Try numeric search
        const numeric = Number(q);
        const isNumeric = !Number.isNaN(numeric);

        let results: DebtorSummaries;

        if (isNumeric) {
          // Match exact ID or partial ID (string contains)
          results = (list ?? []).filter((d) => {
            const idStr = String(d?.id ?? "").toLowerCase();
            return idStr.includes(q);
          });
        } else {
          // Text search
          results = (list ?? []).filter((d) => {
            return (
              (d?.name ?? "").toLowerCase().includes(q) ||
              (d?.email ?? "").toLowerCase().includes(q) ||
              (d?.primaryAddress ?? "").toLowerCase().includes(q) ||
              (d?.deliveryAddress ?? "").toLowerCase().includes(q)
            );
          });
        }

        setFilteredDebtors(results);
        setSearchOpen(true);
      }, 250),
    [],
  );
  useEffect(() => {
    if (refreshFlag === "1") {
      refresh(); // 🔥 force refresh of debtor list
    }
  }, [refreshFlag]);

  return (
    <>
      <TextInput
        disabled={debtorLoading}
        placeholder="Search Customers..."
        leftSection={<IconSearch size={16} />}
        rightSection={
          <IconClearAll
            onClick={() => {
              setSearch("");
              setSearchOpen(false);
            }}
            size={16}
          />
        }
        style={{ width: 250 }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setSearch("");
            setSearchOpen(false);
          }
        }}
        onChange={(e) => {
          const value = e.currentTarget.value;
          setSearch(value);
          debouncedSearch(value, debtors);
        }}
        value={search}
      />
      <Modal
        opened={searchOpen}
        onClose={() => {
          setSearch("");
          setSearchOpen(false);
        }}
        withCloseButton={false}
        closeOnEscape
        closeOnClickOutside
        trapFocus={false} // <-- prevents focus stealing
        centered={false}
        withinPortal // <-- ensures correct z-index without focus trap
        overlayProps={{ backgroundOpacity: 0, blur: 0 }}
        padding={0}
        radius="md"
        styles={{
          content: {
            position: "absolute",
            top: HEADER_HEIGHT + 8,
            left: "50%",
            transform: "translateX(-50%)",
            width: "75vw",
            border: "none",
            background: "transparent",
            boxShadow: "none",
          },
        }}
      >
        <Paper
          radius="md"
          shadow="md"
          withBorder
          style={{
            overflow: "hidden",
            maxHeight: "60vh",
            background: "white",
          }}
        >
          <ScrollArea h="60vh" px="sm" py="xs">
            {filteredDebtors.length === 0 ? (
              <Text size="sm" c="dimmed">
                No matches found
              </Text>
            ) : (
              <Stack gap="sm">
                {filteredDebtors.map((d) => (
                  <DebtorCard
                    key={d.id}
                    d={d}
                    onClose={() => {
                      setSearchOpen(false);
                    }}
                  />
                ))}
              </Stack>
            )}
          </ScrollArea>
        </Paper>
      </Modal>
    </>
  );
};

export default CustomerSearch;
